import { Component, ElementRef, ViewChild, Input, SimpleChanges, OnChanges } from '@angular/core';
import * as d3 from 'd3';
import { detect } from 'detect-browser';

import { AttributeValue } from '../../../../services/dashboard/charts.types';

@Component({
	selector: 'app-gauge',
	templateUrl: './gauge.component.html',
	styleUrls: ['./gauge.component.scss'],
})
export class GaugeComponent implements OnChanges {
	canvas;
	scale;
	color;

	@ViewChild('svgContainer', { static: true })
	svgContainer: ElementRef;
	attributesList: any;

	@Input() chartData: any;

	constructor() {}

	ngOnChanges(changes: SimpleChanges) {
		const width = this.svgContainer.nativeElement.offsetWidth;
		const radius = width / 2;
		const ringWidth = width / 13.34;
		for (const propName in changes) {
			if (Object.prototype.hasOwnProperty.call(changes, propName) && propName === 'chartData') {
				const chng = changes[propName];
				const cur = chng.currentValue;
				this.gaugeBuilder(cur, radius, ringWidth, width);
			}
		}
	}

	gaugeBuilder(cur, radius, ringWidth, width) {
		const { attributeValue }: { attributeValue: AttributeValue } = cur;
		const { attributeScale }: { attributeScale: any } = cur;
		const { pointerColor }: { pointerColor: any } = cur;
		const { chartDisplay }: { chartDisplay: any } = cur;
		const { attributeDescription }: { attributeDescription: any } = cur;
		const { chartName }: { chartName: any } = cur;
		const { unitOfMeasurement }: { unitOfMeasurement: any } = cur;
		let maxValue = attributeScale[attributeScale.length - 1][1];
		if (maxValue === null || maxValue === undefined) {
			// FIXME: this is here as a workaround
			// see FITEHEME-612 for more details
			const maxValues = {
				'BMI': 160,
				'Total Cholesterol': 400,
				'Hemoglobin A1c': 10,
				'Exercise Status': 2000,
				'Happiness': 7,
				'Waist Circumference': 80,
				'LDL': 350,
				'HDL': 150,
				'Triglycerides': 500,
			};
			maxValue = maxValues[chartName];
			attributeScale[attributeScale.length - 1][1] = maxValue;
		}
		const maxAngle = 140;
		const minAngle = -140;
		const minValue = attributeScale[0][0];
		// a linear scale that maps domain values to a percent from 0..1
		this.scale = d3.scaleLinear().range([minAngle, maxAngle]).domain([minValue, maxValue]);
		this.colorChanger(cur.colors);
		this.drawPie(
			attributeDescription,
			attributeScale,
			attributeValue,
			chartDisplay,
			pointerColor,
			ringWidth,
			unitOfMeasurement,
			width,
			radius,
			minAngle,
			maxAngle
		);
	}

	drawPie(
		attributeDescription,
		attributeScale,
		attributeValue,
		chartDisplay,
		pointerColor,
		ringWidth,
		unitOfMeasurement,
		width,
		radius,
		minAngle,
		maxAngle
	) {
		this.createCanvas(chartDisplay, width);
		this.render(attributeScale, ringWidth, width, radius, minAngle, maxAngle);
		this.drawGaugeLabel(pointerColor, chartDisplay, attributeDescription, width, radius);
		this.drawGaugeValue(attributeValue, unitOfMeasurement, pointerColor, chartDisplay, width, radius);
		this.drawPointer(attributeValue, pointerColor, chartDisplay, ringWidth, width, radius, minAngle);
	}

	colorChanger(colorScale: string[]) {
		const browser = detect();
		switch (browser && browser.name) {
			case 'firefox':
				// colorScale.reverse(); // FIXME: firefox will sometimes cause an issue here and sometimes not further debugging is required
				this.color = d3.scaleOrdinal().range(colorScale);
				break;
			default:
				this.color = d3.scaleOrdinal().range(colorScale);
		}
	}

	createCanvas(chartDisplay, width) {
		let clipHeight: number;
		if (chartDisplay === 'multiple') {
			clipHeight = 100;
			d3.selectAll('.c-svg-container.gauge-chart > *').remove();
			this.canvas = d3
				.select('.c-svg-container.gauge-chart')
				.append('svg')
				.attr('class', 'gauge')
				.attr('viewBox', `-25 0 ${width * 1.5} 150`)
				.append('g');
		} else {
			clipHeight = 200;
			d3.selectAll('.c-svg-container.gauge-chart > *').remove();
			this.canvas = d3
				.select('.c-svg-container.gauge-chart')
				.append('svg')
				.attr('class', 'gauge')
				.attr('viewBox', `0 0 ${width} 200`)
				.attr('preserveAspectRatio', 'xMidYMid meet')
				.append('g');
		}
	}

	centerTranslation(width, radius) {
		return 'translate(' + width / 2 + ',' + radius + ')';
	}

	deg2rad(deg) {
		return (deg * Math.PI) / 180;
	}

	render(attributeScale, ringWidth, width, radius, minAngle, maxAngle) {
		const rangeDivider = [0.1, 0.3, 0.2, 0.3, 0.1]; /* chart segmentattion range  */

		const ticks = this.scale.ticks(attributeScale.length);
		const tickData = d3.range(attributeScale.length).map(() => {
			return 1 / attributeScale.length;
		});
		const centerTx = this.centerTranslation(width, radius);

		const arcs = this.canvas.append('g').attr('class', 'arc').attr('transform', centerTx);
		const pieLayout = d3
			.pie()
			.padAngle(0.01)
			.value((d: any) => {
				return d[1];
			})
			.sort((d: any, i: number) => {
				return i;
			})
			.startAngle(this.deg2rad(minAngle))
			.endAngle(this.deg2rad(maxAngle));
		const ringInset = width / 10;
		const arc = d3
			.arc()
			.innerRadius(radius - ringWidth - ringInset)
			.outerRadius(radius - ringInset);
		arcs
			.selectAll('path')
			.data(pieLayout(attributeScale))
			.enter()
			.append('path')
			.attr('fill', (d, i) => {
				return this.color(i);
			})
			.attr('d', arc);
	}

	drawGaugeLabel(pointerColor, chartDisplay, attributeDescription, width, radius) {
		let labelSize = width / 8;
		// FIXME: I'm sure there is a better way with d3
		// to dynamically resize the text to fit within the
		// viewBox of the svg and to respond to greater
		// ranges of text
		if (chartDisplay === 'multiple') {
			if (attributeDescription.length > 9) {
				labelSize = labelSize / 1.5;
			} else if (attributeDescription.length > 15) {
				labelSize = labelSize / 1.7;
			} else if (attributeDescription.length > 25) {
				labelSize = labelSize / 2;
			}
		} else {
			if (attributeDescription.length > 9) {
				labelSize = labelSize / 1.4;
			} else if (attributeDescription.length > 15) {
				labelSize = labelSize / 1.6;
			} else if (attributeDescription.length > 25) {
				labelSize = labelSize / 1.9;
			}
		}
		const gaugeLabel = this.canvas
			.append('g')
			.attr('class', 'gaugeLabel')
			.attr('transform', this.centerTranslation(width, radius))
			.append('text')
			.text(`${attributeDescription}`)
			.attr('fill', pointerColor)
			.style('text-anchor', 'middle')
			.style('font-size', `${labelSize}px`)
			.style('font-weight', 'bold')
			.style('margin-bottom', '10px');
	}

	drawGaugeValue(patientAttribute, unitOfMeasurement, pointerColor, chartDisplay, width, radius) {
		let valueSize = width / 10;
		if (chartDisplay === 'multiple') {
			valueSize = valueSize / 1.1;
		}
		let displayValue = patientAttribute;
		if (unitOfMeasurement != null) {
			displayValue = `${displayValue} ${unitOfMeasurement}`;
		}
		const tranlateY = radius + 10;
		const gaugeValue = this.canvas
			.append('g')
			.attr('class', 'gaugeValue')
			.attr('transform', 'translate(' + width / 2 + ',' + tranlateY + ')')
			.append('text')
			.attr('y', 18)
			.text(displayValue)
			.attr('fill', pointerColor)
			.style('text-anchor', 'middle')
			.style('font-size', `${valueSize}px`)
			.style('font-weight', 'normal');
	}

	drawPointer(patientAttribute, pointerColor, chartDisplay, ringWidth, width, radius, minAngle) {
		const labelInset = 10;
		const newAngle = this.scale(patientAttribute);
		const pointerLine = d3.symbolTriangle;
		const newLinePosition = labelInset - radius + ringWidth - 5;
		const transitionMs = 500;
		let newPointerPosition = labelInset - radius + ringWidth;
		let lineSize;
		let symbolSize;
		if (chartDisplay === 'multiple') {
			newPointerPosition += 12;
			lineSize = '6%';
			symbolSize = 100;
		} else {
			newPointerPosition += 26;
			lineSize = '10%';
			symbolSize = 200;
		}
		const symbol = d3.symbol().size(symbolSize);
		const pg = this.canvas
			.append('g')
			.attr('class', 'pointer')
			.attr('transform', this.centerTranslation(width, radius));

		// Drawing a line that is where the Patient Data
		const line = pg.append('rect').attr('width', 2).attr('height', lineSize).attr('fill', 'black');

		const pointer = pg.append('path').attr('d', symbol.type(pointerLine)).style('fill', pointerColor);

		pointer
			.transition()
			.duration(transitionMs)
			.attrTween('transform', interpolate => {
				return d3.interpolate(
					'rotate(' + minAngle + ') translate(' + minAngle + ', 0)',
					'rotate(' + newAngle + ') translate(0, ' + newPointerPosition + ')'
				);
			});
		line
			.transition()
			.duration(transitionMs)
			.attrTween('transform', interpolate => {
				return d3.interpolate(
					'rotate(' + minAngle + ') translate(' + minAngle + ', 0)',
					'rotate(' + newAngle + ') translate(-1, ' + newLinePosition + ')'
				);
			});
	}
}
